import React from 'react';
import { BookOpen, ChevronRight, Sparkles } from 'lucide-react';

/**
 * GrammarWidget - Sidebar widget showing grammar points in current verse
 * 
 * Props:
 * - grammarPoints: Array of grammar point objects
 * - onSelectGrammar: Callback when a grammar point is clicked
 * - language: 'hebrew' or 'greek'
 * - isLoading: Boolean to show loading state
 */
const GrammarWidget = ({ grammarPoints = [], onSelectGrammar, language = 'hebrew', isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm font-semibold text-white">Grammar Points</h3>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-slate-700/50 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!grammarPoints || grammarPoints.length === 0) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-amber-500" />
          <h3 className="text-sm font-semibold text-white">Grammar Points</h3>
        </div>
        <p className="text-slate-500 text-sm text-center py-4">
          Grammar analysis coming soon
        </p>
      </div>
    );
  }

  // Group by type if available
  const groupedPoints = grammarPoints.reduce((acc, point) => {
    const type = point.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(point);
    return acc;
  }, {});

  const typeLabels = {
    verb_stem: 'Verb Stem',
    verb_form: 'Verb Form',
    verb_conjugation: 'Conjugation',
    construct: 'Construct',
    article: 'Article',
    preposition: 'Preposition',
    agreement: 'Agreement',
    case: 'Case',
    tense: 'Tense',
    voice: 'Voice',
    mood: 'Mood',
    other: 'Other'
  };

  const typeColors = {
    verb_stem: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    verb_form: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    verb_conjugation: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    construct: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    article: 'bg-green-500/20 text-green-400 border-green-500/30',
    preposition: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    agreement: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    case: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    tense: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    voice: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    mood: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    other: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-amber-500" />
        <h3 className="text-sm font-semibold text-white">Grammar in this verse</h3>
        <span className="ml-auto text-xs text-slate-500">{grammarPoints.length}</span>
      </div>

      <div className="space-y-3">
        {Object.entries(groupedPoints).map(([type, points]) => (
          <div key={type}>
            <div className="text-xs text-slate-500 mb-1.5">{typeLabels[type] || type}</div>
            <div className="space-y-1.5">
              {points.map((point, idx) => (
                <button
                  key={point.id || idx}
                  onClick={() => onSelectGrammar?.(point)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-all
                    hover:scale-[1.02] hover:shadow-lg
                    ${typeColors[type] || typeColors.other}`}
                  data-testid={`grammar-point-${point.id || idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">{point.name}</span>
                      {point.hebrew_name && (
                        <span className="ml-2 text-xs opacity-75 font-hebrew">{point.hebrew_name}</span>
                      )}
                      {point.greek_name && (
                        <span className="ml-2 text-xs opacity-75">{point.greek_name}</span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-50" />
                  </div>
                  {point.word_refs && point.word_refs.length > 0 && (
                    <div className="mt-1 text-xs opacity-75">
                      {language === 'hebrew' ? (
                        <span className="font-hebrew">{point.word_refs.join('، ')}</span>
                      ) : (
                        <span>{point.word_refs.join(', ')}</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Coming Soon Badge */}
      <div className="mt-4 pt-4 border-t border-slate-700/50">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Sparkles className="w-3 h-3" />
          <span>AI grammar analysis in development</span>
        </div>
      </div>
    </div>
  );
};

export default GrammarWidget;
